import type { ReactNode } from "react";

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { motion } from "framer-motion";
import { ReactElement, useState } from "react";

import { Entity } from "../api";
import { $dragData, $isDragInProgress } from "../stores/graph/ui.ts";
import { DragData } from "../types/graph-ui.ts";
import { IconRenderer } from "./icons/IconRenderer";
import { NodePreview } from "./NodePreview";

// Type guard to check if drag data has the expected structure
const isValidDragData = (data: unknown): data is DragData => {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  if (!("entity" in obj) || !("text" in obj)) {
    return false;
  }

  const entity = obj.entity;
  if (typeof entity !== "object" || entity === null) {
    return false;
  }

  const entityObj = entity as Record<string, unknown>;
  if (!("id" in entityObj) || typeof entityObj.id !== "string") {
    return false;
  }

  if (typeof obj.text !== "string") {
    return false;
  }

  return true;
};

interface DndProviderProps {
  children: ReactNode;
  onDragEnd: (event: DragEndEvent) => void;
}

export const DndProvider = ({
  children,
  onDragEnd,
}: DndProviderProps): ReactElement => {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedType, setDraggedType] = useState<Entity | null>(null);
  const [isFromFloatingContainer, setIsFromFloatingContainer] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent): void => {
    setIsDragging(true);
    $isDragInProgress.set(true);

    // Store the drag data immediately when drag starts - with validation
    if (
      event.active.data.current &&
      isValidDragData(event.active.data.current)
    ) {
      $dragData.set(event.active.data.current);
    }

    const entity = (event.active.data.current as { entity: Entity })?.entity;
    setDraggedType(entity);

    // Check if the drag is coming from the floating container
    const isFromFloating = event.active.id
      .toString()
      .startsWith("floating-notes-");
    setIsFromFloatingContainer(isFromFloating);
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    onDragEnd(event);
    // Set a small timeout to prevent the visual return animation
    requestAnimationFrame(() => {
      setIsDragging(false);
      setDraggedType(null);
      setIsFromFloatingContainer(false);
      $isDragInProgress.set(false);
      $dragData.set(null); // Clear the stored drag data
    });
  };

  return (
    <DndContext
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      {children}
      <DragOverlay dropAnimation={null} modifiers={[]}>
        {isDragging && draggedType && (
          <div className="cursor-grabbing">
            {isFromFloatingContainer ? (
              // Custom overlay for floating container - same as NodePreview for sticky notes
              <motion.div
                animate={{
                  opacity: 1,
                  rotate: [-2, 2, -2],
                  scale: 1,
                }}
                className="flex origin-top select-none items-center justify-center"
                initial={{ opacity: 0, scale: 0.98 }}
                style={{
                  height: "48px",
                  minHeight: "48px",
                  minWidth: "48px",
                  width: "48px",
                }}
                transition={{
                  duration: 0.2,
                  rotate: {
                    duration: 2,
                    ease: "easeInOut",
                    repeat: Infinity,
                    times: [0, 0.5, 1],
                  },
                }}
              >
                <motion.div
                  animate={{
                    boxShadow: [
                      "0 6px 12px rgba(0,0,0,0.15)",
                      "0 4px 8px rgba(0,0,0,0.1)",
                      "0 6px 12px rgba(0,0,0,0.15)",
                    ],
                    scale: [1.01, 1, 1.01],
                    y: [-2, 0, -2],
                  }}
                  className="flex items-center justify-center rounded-lg border-2"
                  style={{
                    backgroundColor: "#374151", // Dark grey background to match graph
                    borderColor: "#6B7280", // Grey border
                    flexShrink: 0,
                    height: "48px",
                    minHeight: "48px",
                    minWidth: "48px",
                    width: "48px",
                  }}
                  transition={{
                    boxShadow: {
                      duration: 2,
                      ease: "easeInOut",
                      repeat: Infinity,
                      times: [0, 0.5, 1],
                    },
                    scale: {
                      duration: 2,
                      ease: "easeInOut",
                      repeat: Infinity,
                      times: [0, 0.5, 1],
                    },
                    y: {
                      duration: 2,
                      ease: "easeInOut",
                      repeat: Infinity,
                      times: [0, 0.5, 1],
                    },
                  }}
                >
                  <IconRenderer
                    icon={draggedType.icon}
                    style={{
                      color: "#FFFFFF", // White text to match graph
                      height: "20px",
                      width: "20px",
                    }}
                  />
                </motion.div>
              </motion.div>
            ) : (
              <NodePreview entity={draggedType} />
            )}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};
